// Sheets (critique decision 9): a scrim, a close button, Escape, a focus trap, and focus back to the
// opener on close. A sheet hangs from the top so it never covers the live edge at the bottom.
import { useEffect, useId, useRef, type ReactNode } from "react";
import { useI18n } from "../i18n";
import { CloseIcon } from "./icons";

const FOCUSABLE = 'button:not([disabled]), input:not([disabled]), [href], [tabindex]:not([tabindex="-1"])';

export function Sheet({
  title,
  onClose,
  children,
  spec,
  sub,
}: {
  /** a string, or (MAJLIS-163) text with an isolated LTR room code inside it */
  title: ReactNode;
  onClose: () => void;
  children: ReactNode;
  spec: string;
  /** MAJLIS-163 (UI-C14): one muted line under the title ("Started 9:12 · you are the host", "You are the host") */
  sub?: ReactNode;
}) {
  const { t } = useI18n();
  const ref = useRef<HTMLDivElement>(null);
  const titleId = useId();
  const closeRef = useRef(onClose);
  useEffect(() => {
    closeRef.current = onClose;
  }, [onClose]);

  useEffect(() => {
    const opener = document.activeElement as HTMLElement | null;
    const box = ref.current!;
    const first = box.querySelector<HTMLElement>("input, " + FOCUSABLE);
    first?.focus();
    const onKey = (ev: KeyboardEvent) => {
      if (ev.key === "Escape") {
        ev.preventDefault();
        closeRef.current();
        return;
      }
      if (ev.key !== "Tab") return;
      const items = Array.from(box.querySelectorAll<HTMLElement>(FOCUSABLE));
      if (items.length === 0) return;
      const [head, tail] = [items[0], items[items.length - 1]];
      if (ev.shiftKey && document.activeElement === head) {
        ev.preventDefault();
        tail.focus();
      } else if (!ev.shiftKey && document.activeElement === tail) {
        ev.preventDefault();
        head.focus();
      } else if (!box.contains(document.activeElement)) {
        ev.preventDefault();
        head.focus();
      }
    };
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("keydown", onKey);
      if (opener && document.contains(opener)) opener.focus();
    };
  }, []);

  return (
    <div className="sheet-layer">
      <div className="scrim" data-testid="scrim" onClick={() => onClose()} />
      <div className="sheet" role="dialog" aria-modal="true" aria-labelledby={titleId} ref={ref} data-spec={spec}>
        <div className="sheet-head">
          {sub ? (
            <div className="sheet-heading">
              <h2 id={titleId} className="sheet-title">
                {title}
              </h2>
              <p className="sheet-sub">{sub}</p>
            </div>
          ) : (
            <h2 id={titleId} className="sheet-title">
              {title}
            </h2>
          )}
          <button type="button" className="icon-button" aria-label={t("close")} onClick={() => onClose()}>
            <CloseIcon />
          </button>
        </div>
        {children}
      </div>
    </div>
  );
}
